import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MenuService } from '@tbtk/menu';
import * as bootstrap from 'bootstrap';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { FileExtensions, FileTypeIconUrls, FileTypes } from 'src/app/enums/file-types.enum';
import { Breadcrumb } from 'src/app/libs/breadcrumb/breadcrumb.service';
import { FolderHierarchy } from 'src/app/models/folder-hierarchy.model';
import { PublicSharingsModel } from 'src/app/models/share-links.model';
import { TotalSharesModel } from 'src/app/models/shares.model';
import { StorageModel } from 'src/app/models/storage.model';
import { TreeNode } from 'src/app/models/template.model';
import { UserQuota } from 'src/app/models/upload.model';
import { MyStorageService } from 'src/app/pages/storage/storage.service';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as SgeCrypto from 'src/assets/lib/sge/sge-crypto';

import { NotificationService } from './notification.service';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  multipleSelectedItems$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  selectedFolderHierarchy$: BehaviorSubject<FolderHierarchy> = new BehaviorSubject<FolderHierarchy>({} as FolderHierarchy);
  sharesMultipleSelectedItems$: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  isGridViewSelected$ = new BehaviorSubject<boolean>(true);
  viewStrategy$ = new Subject<string>();
  breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>([]);
  isActionBarRenameClicked = new Subject<void>();
  isTransactionHistoryClicked = new BehaviorSubject<boolean>(false);
  isSharedWithMeClicked = new BehaviorSubject<boolean>(false);
  isDetailClicked = new BehaviorSubject<boolean>(false);
  isListViewDetailClicked: Subject<boolean> = new Subject<boolean>();
  isShareDetailClicked = new BehaviorSubject<boolean>(false);
  isShareManagementModalOpened = new BehaviorSubject<boolean>(false);
  isCopyClicked = new BehaviorSubject<boolean>(false);
  multiChoice = false;
  selectedItems: any[] = [];
  selectedRowIds: string[] = [];
  sharesSelectedItems: TotalSharesModel[] = [];
  rebindListener: Subject<void> = new Subject<void>();
  manageLinkSuccessResponse = new BehaviorSubject<any>([]);
  currentRoute: Subject<string> = new Subject<string>();
  chosenDocument: Subject<string> = new Subject<string>();
  openRecentDetailFromList: Subject<boolean> = new Subject<boolean>();
  sendListInfoToManageLink: Subject<PublicSharingsModel> = new Subject<PublicSharingsModel>();
  navigateTransactionHistory: Subject<boolean> = new Subject<boolean>();
  quotaChange$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isInPubliclyShared: Subject<boolean> = new Subject<boolean>();
  firstFolderShared!: StorageModel[];
  showNewButton: Subject<boolean> = new Subject<boolean>();
  selectedRowItem: Subject<any> = new Subject<any>();
  urlId: Subject<string> = new Subject<string>();
  secureStorageList!: boolean;
  secureStorage = false;
  cancelCreatedLink: Subject<boolean> = new Subject<boolean>();
  cancelShares: Subject<boolean> = new Subject<boolean>();
  isDocumentTemplateSelected: Subject<boolean> = new Subject<boolean>();
  selectedTemplate?: TreeNode;
  selectedTemplate$: Subject<TreeNode> = new Subject<any>();
  groupMembersUpdated: Subject<boolean> = new Subject<boolean>();
  multipleObject = false;
  modalFooterClicked!: boolean;
  favoritedEncryptedFolderObjectId!: string;
  publicShareId!: string;
  decryptFolderName = true;
  fromWhere!: string;
  objectName = '';
  selectedId!: number;

  encryptedItem = {
    encrypted: true,
    directory: true,
    objectName: 'Şifreli Klasör',
  };

  private openedModalId = new Subject<string>();
  openedModalId$ = this.openedModalId.asObservable();

  private isEncrypted = new Subject<boolean>();
  isEncrypted$ = this.isEncrypted.asObservable();

  private folderTreeModalOpened = new Subject<boolean>();
  folderTreeModalOpened$ = this.folderTreeModalOpened.asObservable();

  private objectId = new BehaviorSubject<string>('');
  objectId$ = this.objectId.asObservable();

  private encryptedId = new BehaviorSubject<string>('');
  encryptedId$ = this.encryptedId.asObservable();

  private selectedRows = new BehaviorSubject<any[]>([]);
  selectedRows$ = this.selectedRows.asObservable();

  private isRenamingActive = new Subject<boolean>();
  isRenamingActive$ = this.isRenamingActive.asObservable();

  private securedFolder = new Subject<boolean>();
  securedFolder$ = this.securedFolder.asObservable();

  constructor(
    private http: HttpClient,
    private menuService: MenuService,
    private myStorageService: MyStorageService,
    private notificationService: NotificationService,
    private router: Router,
    private translateService: TranslateService,
  ) {}

  translateLabel(label: string): string {
    let translatedLabel = label;
    this.translateService.get(label).subscribe((translation: string) => {
      translatedLabel = translation || label;
    });

    return translatedLabel;
  }

  quotaHasChanged(): void {
    this.quotaChange$.next(true);
  }

  getImageUrlByExtension(item: any): string {
    // multiple object
    if (item.multipleObject) {
      // encrypted multiple object
      if (item.encrypted) {
        return FileTypeIconUrls[FileTypes.MULTIPLE_SECURE_FILES];
      }
      // unencrypted multiple object
      else {
        return FileTypeIconUrls[FileTypes.MULTIPLE_FILES];
      }
    } // directory
    else if (item.directory) {
      // encrypted directory
      if (item.encrypted) {
        return FileTypeIconUrls[FileTypes.FOLDER_SECURE];
      }
      // unencrypted directory
      else {
        return FileTypeIconUrls[FileTypes.FOLDER];
      }
    } // file
    else if (item) {
      // encrypted file
      if (item.encrypted) {
        return FileTypeIconUrls[FileTypes.FILE_SECURE];
      }

      let extension = '';
      if (item.name) {
        extension = item.name.split('.').pop()?.toLowerCase();
      } else if (item.fileName) {
        extension = item.fileName.split('.').pop()?.toLowerCase();
      } else if (item.objectName) {
        extension = item.objectName?.split('.').pop()?.toLowerCase();
      } else {
        extension = this.objectName.split('.').pop()?.toLowerCase() || '';
      }

      return FileTypeIconUrls[FileExtensions[extension]] || FileTypeIconUrls[FileTypes.UNKNOWN];
    } // unknown
    else {
      return FileTypeIconUrls[FileTypes.UNKNOWN];
    }
  }

  restrictSpecialCharacterFromGroupName(groupName: string, folder?: boolean): boolean {
    let format: RegExp;

    if (folder) {
      format = /[!@#$%^&*+=\[\]{};':"\\|,.<>\/?]+/;
    } else {
      format = /[!@#$%^&*+=\[\]{};':"\\|,.<>\/?]+/;
    }

    return format.test(groupName.replace('_', ''));
  }

  isEmail(value: string): boolean {
    const trimmedValue = value.trim(); // Değerin başındaki ve sonundaki boşlukları kaldırır
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    const email = /^[a-zA-Z0-9._%+-çÇşŞğĞıİöÖüÜ]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

    return email.test(trimmedValue);
  }

  checkCharactersSupported(name: string): boolean {
    let isOk = true;
    if (!name.trim().match(/^(?!^(PRN|AUX|CLOCK\$|NUL|CON|COM\d|LPT\d|\s.*)(\..+)?$)[^\x00-\x1f\\<>^?*:\"/|]*([^\.|\x20|\x00-\x1f\\<>^?*:\"/]+)$/gi)) {
      isOk = false;
    }

    return isOk;
  }

  getName(name: string): string {
    const parts = name.split('.');
    if (parts.length > 1) {
      parts.pop(); // Uzantıyı kaldırırız
    }
    const extensionlessName = parts.join('.');

    return extensionlessName;
  }

  finishRenaming(item: any): boolean {
    const inputElement = document.getElementById('renameInput-' + item?.objectId) as HTMLInputElement;
    if (inputElement) {
      this.setIsRenamingActive(false);
      if (inputElement.value === '') {
        const notification = {
          message: 'İsim boş girilemez.',
        };
        this.notificationService.warningNotification(notification);

        return false;
      }
      if (item.objectName) {
        if (!this.checkCharactersSupported(item.objectName)) {
          inputElement.classList.add('invalid-input');
          const notification = {
            message: 'Geçersiz karakter girdiniz.',
          };
          this.notificationService.warningNotification(notification);

          return false;
        } else if (item.originalName.length > 50) {
          inputElement.classList.add('invalid-input');
          const notification = {
            message: 'Karakter sayısı 50 ile sınırlıdır.',
          };
          this.notificationService.warningNotification(notification);

          return false;
        } else if (item.objectName !== item.originalName) {
          inputElement.classList.remove('invalid-input');
          if (item) {
            const newName = item.originalName;
            if (!item.encrypted) {
              this.myStorageService.renameFileOrFolder(item.objectId, newName).subscribe({
                next: () => {
                  const notification = {
                    message: this.getTranslatedMessage('OPERATION_SUCCESSFUL'),
                  };
                  this.notificationService.successNotification(notification);
                  item.objectName = newName;
                },
              });
            } else {
              let parentEncryptedId = '';
              parentEncryptedId = item.parentEncryptedId;
              SgeCrypto.listEncryptedFolder(parentEncryptedId).then((objList: any) => {
                const objIndex = objList.findIndex((file: any) => file.name === newName);
                if (objIndex !== -1) {
                  const notification = {
                    message: 'Aynı isimde dosya/klasör mevcut.',
                  };
                  this.notificationService.warningNotification(notification);

                  return;
                } else {
                  if (item.directory) {
                    SgeCrypto.renameEncryptedFolder(item.encryptedId, newName, true);
                    this.rebindListener.next();
                  } else {
                    SgeCrypto.renameEncryptedFile(item.encryptedId, newName, true);
                    this.rebindListener.next();
                  }
                }
              });
            }
          }

          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  getUserQuota(): Observable<UserQuota> {
    return this.http.get<UserQuota>(`/api/s3/upload/usage`);
  }

  getFileExtension(fileName: string): string {
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex !== -1 && lastDotIndex !== 0 && lastDotIndex !== fileName.length - 1) {
      return fileName.slice(lastDotIndex).toLowerCase();
    }

    return '';
  }

  getFileNameWithoutExtension(fileName: string): string {
    if (fileName && fileName.includes('.')) {
      const lastDotIndex = fileName.lastIndexOf('.');
      if (lastDotIndex !== -1 && lastDotIndex !== 0) {
        return fileName.slice(0, lastDotIndex);
      }

      return fileName;
    }

    return fileName;
  }

  handleMultiChoice(item: any, event?: MouseEvent, draggingId?: string): void {
    if (this.selectedItems.length === 1) {
      this.finishRenaming(this.selectedItems[0]);
    }
    const el = event?.target as HTMLElement;
    if (el && !el.classList.contains('ti-3-dot')) {
      this.menuService.findComponent('itemOptions')?.component.close();
    }
    if (item) {
      if (event?.ctrlKey || event?.metaKey) {
        if (this.selectedItems.includes(item)) {
          const index = this.selectedItems.indexOf(item);
          this.selectedItems.splice(index, 1);
          if (draggingId) {
            const draggingIndex = this.selectedRowIds.indexOf(draggingId);
            this.selectedRowIds.splice(draggingIndex, 1);
          }
        } else {
          this.selectedItems.push(item);
          if (draggingId) {
            this.selectedRowIds.push(draggingId);
          }
        }
      } else {
        if (this.selectedItems.length === 1 && item.objectId === this.selectedItems[0].objectId) {
          this.selectedItems = [];
          if (draggingId) {
            this.selectedRowIds = [];
          }
        } else {
          this.selectedItems = [item];
          if (draggingId) {
            this.selectedRowIds = [draggingId];
          }
        }
      }
    } else {
      this.selectedItems = [];
      if (draggingId) {
        this.selectedRowIds = [];
      }
    }
    this.setSelectedRows(this.selectedRowIds);
    this.selectedItems = this.removeDuplicatesByObjectId(this.selectedItems);
    this.multipleSelectedItems$.next(this.selectedItems);
    this.multiChoice = this.selectedItems.length > 1;
  }

  removeDuplicatesByObjectId(items: any[]): any[] {
    const uniqueItemsMap: Map<string, any> = new Map();

    items.forEach((item) => {
      if (!uniqueItemsMap.has(item.objectId)) {
        uniqueItemsMap.set(item.objectId, item);
      }
    });

    return Array.from(uniqueItemsMap.values());
  }

  showEncryptedNonEncryptedTogetgerNotification(selectedItems: any): void {
    const hasMixedValues =
      selectedItems?.some((item: any) => item?.encrypted !== undefined && item.encrypted === true) &&
      selectedItems?.some((item: any) => item?.encrypted !== undefined && item.encrypted === false);
    if (hasMixedValues) {
      const notification = {
        message: this.getTranslatedMessage('ENCRYPTED_AND_UNENCRYPTED_FILES_CANNOT_BE_PROCESS_TOGETHER'),
      };
      this.notificationService.warningNotification(notification);
      this.multipleSelectedItems$.next([]);
      this.sharesMultipleSelectedItems$.next([]);
      this.selectedItems = [];
    }
  }

  showHasMixedValuesOrNot(selectedItems: any): boolean {
    const hasMixedValues =
      selectedItems?.some((item: any) => item?.encrypted !== undefined && item.encrypted === true) &&
      selectedItems?.some((item: any) => item?.encrypted !== undefined && item.encrypted === false);

    return hasMixedValues;
  }

  isAllMultipleObject(selectedItems: any): boolean {
    if (!selectedItems || selectedItems.length <= 1) {
      return false;
    }

    const allMultipleObjectTrue = selectedItems.every((item: any) => item.multipleObject === true);

    return allMultipleObjectTrue;
  }

  isPreviewOnLibreSupported(fileExt: any): boolean {
    const supportedLibrePreviewExt = ['.doc', '.docx', '.xls', '.xlsx', '.xlsm', '.ppt', '.pptx', '.pdf', '.txt', '.dot', '.dotx', '.odt', '.odp', '.ods', '.csv'];
    if (supportedLibrePreviewExt.indexOf(fileExt) !== -1) {
      return true;
    } else {
      return false;
    }
  }

  handleSharesMultiChoice(item: any, event?: MouseEvent): void {
    const el = event?.target as HTMLElement;
    if (el && !el.classList.contains('ti-3-dot')) {
      this.menuService.findComponent('itemOptions')?.component.close();
    }
    if (item) {
      if (event?.ctrlKey || event?.metaKey) {
        if (this.sharesSelectedItems.includes(item)) {
          const index = this.sharesSelectedItems.indexOf(item);
          this.sharesSelectedItems.splice(index, 1);
        } else {
          this.sharesSelectedItems.push(item);
        }
      } else {
        if (this.sharesSelectedItems.length === 1 && item.objectId === this.sharesSelectedItems[0].objectId) {
          this.sharesSelectedItems = [];
        } else {
          this.sharesSelectedItems = [item];
        }
      }
    } else {
      this.sharesSelectedItems = [];
    }
    this.multiChoice = this.sharesSelectedItems.length > 1;
    this.sharesMultipleSelectedItems$.next(this.sharesSelectedItems);
  }

  isBase64Image(content: any): any {
    const pattern = /^data:image\/([a-z]+);base64,/;

    return pattern.test(content);
  }

  routeToEncryptedLogin(fromWhere?: string): void {
    if (fromWhere) {
      this.fromWhere = fromWhere;
    }
    this.router.navigateByUrl('/sifreli-depo/giris');
  }

  showModal(id: string): void {
    this.setOpenedModalId(id);
    setTimeout(() => {
      const modalElement = document.querySelector(id);
      if (modalElement) {
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
      }
    }, 0);
  }

  collectObjectIds(arr: any[]): string[] {
    return arr.filter((obj) => obj && obj.objectId !== undefined).map((obj) => obj.objectId);
  }

  collectEncryptedIds(arr: any[]): any {
    return arr.filter((obj) => obj && obj.encryptedId !== undefined).map((obj) => obj.encryptedId);
  }

  getObjectLength(obj: any): number {
    return Object.keys(obj).length;
  }

  hasEncryptedObject(arr: any): boolean {
    return arr.some((obj: any) => {
      const isEncrypted = obj && obj.encrypted === true;

      return isEncrypted;
    });
  }

  hasDirectoryItem(arr: any[] | undefined): boolean {
    if (arr && arr.length === 1) {
      const item = arr[0];

      return !!item?.directory === true && !!item?.encrypted === true;
    }

    if (arr) {
      const hasTrueEncryptedAndTrueDirectory = arr.some((obj: any) => !!obj && obj.directory === true && !!obj.encrypted === true);
      const hasTrueEncryptedAndFalseDirectory = arr.some((obj: any) => !!obj && obj.directory === false && !!obj.encrypted === true);

      return hasTrueEncryptedAndTrueDirectory || hasTrueEncryptedAndFalseDirectory;
    } else {
      return false;
    }
  }

  isEmptyObject(obj: Record<string, any>): boolean {
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        return false;
      }
    }

    return true;
  }

  hasMultiShare(shareArray: TotalSharesModel[] = []): boolean {
    return shareArray.some((share) => share.shareName !== null);
  }

  convertToReadableFormat(value: number): string {
    let displayedNumber!: string;
    if (value >= 1099511627776) {
      // 1 TB = 1099511627776 bytes
      displayedNumber = Math.round((value / 1099511627776) * 10) / 10 + ' TB';
    } else if (value >= 1073741824) {
      // 1 GB = 1073741824 bytes
      displayedNumber = Math.round((value / 1073741824) * 10) / 10 + ' GB';
    } else if (value >= 1048576) {
      // 1 MB = 1048576 bytes
      displayedNumber = Math.round((value / 1048576) * 10) / 10 + ' MB';
    } else if (value >= 1024) {
      // 1 KB = 1024 bytes
      displayedNumber = Math.round((value / 1024) * 10) / 10 + ' KB';
    } else {
      displayedNumber = value + ' B';
    }

    return displayedNumber;
  }

  containsTurkishCharacters(text: string): boolean {
    const turkishCharacters = /[şğüöçıŞĞÜÖÇİ]/;

    return turkishCharacters.test(text.normalize('NFC'));
  }

  getCurrentTimezone(): string {
    const offsetMinutes = new Date().getTimezoneOffset();
    const offsetHours = Math.abs(offsetMinutes / 60);

    const offsetSign = offsetMinutes < 0 ? '+' : '-';
    const offsetHoursString = offsetHours < 10 ? '0' + offsetHours : '' + offsetHours;
    const offsetMinutesString = (Math.abs(offsetMinutes) % 60 < 10 ? '0' : '') + (Math.abs(offsetMinutes) % 60);

    return `${offsetSign}${offsetHoursString}${offsetMinutesString}`;
  }

  decryptOneFileOrFolderName(listviewItem: any): void {
    if (listviewItem.directory) {
      SgeCrypto.getFolderMetadata(listviewItem.encryptedId).then((file: any) => {
        listviewItem.name = file.name;
      });
    } else {
      SgeCrypto.getFileMetadata(listviewItem.encryptedId).then((file: any) => {
        listviewItem.name = file.name;
      });
    }
  }

  decryptOneFolderName(listviewItem: any): Promise<string> {
    return new Promise((resolve, reject) => {
      SgeCrypto.getFolderMetadata(listviewItem.encryptedId)
        .then((file: any) => {
          console.log('name', file.name);
          listviewItem.name = file.name;
          resolve(listviewItem.name); // işlem tamamlandığında ismi resolve ediyoruz
        })
        .catch((error: any) => {
          reject(error); // hata oluştuğunda reject ediyoruz
        });
    });
  }

  setOpenedModalId(id: string): void {
    this.openedModalId.next(id);
  }

  setIsEncrypted(value: boolean): void {
    this.isEncrypted.next(value);
  }

  setFolderTreeModalOpened(value: boolean): void {
    this.folderTreeModalOpened.next(value);
  }

  setObjectId(objectId: string | null): void {
    if (objectId) this.objectId.next(objectId);
  }

  setEncryptedId(id: string): void {
    this.encryptedId.next(id);
  }

  setSelectedRows(rows: string[]): void {
    this.selectedRows.next(rows);
  }

  setIsRenamingActive(value: boolean): void {
    this.isRenamingActive.next(value);
  }

  setSecuredFolder(value: boolean): void {
    this.securedFolder.next(value);
  }

  getTranslatedMessage(key: string, params: { [key: string]: string } = {}): string {
    let translation = this.translateService.instant(key);
    if (translation) {
      Object.keys(params).forEach((paramKey) => {
        translation = translation.replace(`{${paramKey}}`, params[paramKey]);
      });

      return translation;
    }

    return key;
  }

  setSelectedTemplate(item: any): void {
    this.selectedTemplate = item;
    this.selectedTemplate$.next(item);
  }

  getSelectedTemplate(): any {
    return this.selectedTemplate;
  }

  getMinDate(): string {
    const today = new Date();
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const day = today.getDate().toString().padStart(2, '0');
    const year = today.getFullYear();

    return `${year}-${month}-${day}T00:00`;
  }

  removeLastWord(str: string): string {
    const words = str.split(' ');
    if (words.length === 1) {
      return str;
    }
    const newWords = words.slice(0, -1);

    return newWords.join(' ');
  }
}
